import PropTypes from "prop-types";
import React, { Fragment } from "react";
import { ToastService } from "../../../../components/toasts/Toasts";
import sessionStore from "../../../../services/session/SessionStore";
import { approvePost, rejectPost, updatePost } from "../../publishing/api";
import {
  canApproveResponses,
  canDraftResponses,
  canDeletePendingReply
} from "../../publishing/postActions/ToolbarActions";
import PostToBePublishedStatus from "../../publishing/PostToBePublishedStatus";
import { observer } from "mobx-react";
import { deletePost } from "../../socialApi";
import modalService from "../../../../components/modal/modalService"
import { Confirm } from "../../../../components";
/**
 * Renders reply editor and approve/reject
 */
export default observer(class SocialReplyEditor extends React.Component {
  static propTypes = {
    // the NewPostMessage
    item: PropTypes.any.isRequired,
    onReject: PropTypes.func.isRequired,
    onApprove: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
  };

  static defaultProps = {
    onReject: () => {},
    onApprove: () => {},
    onDelete: () => {}
  };

  state = {
    editingText: "",
    rejectedText: "",
    isEditing: false,
    isUpdatingPost: false
  };

  saveAndApprovePost = async () => {
    const isApproved = true;
    const toastSuccessTitle = "Successfully Approved";
    const toastSuccessMessage = "Your post has been approved and scheduled to be submitted";
    this.savePost(isApproved, toastSuccessTitle, toastSuccessMessage);
  };

  deletePost = () => {
    const item = this.props.item;
    modalService.requestModalV2({ modal: <Confirm
      visible={true}
      toggle={modalService.closeModal}
      header="Are you sure want to cancel this post?"
      message="Once deleted, the post cannot be recovered."
      confirmText="Delete"
      onContinue={async () => {
        this.setState({
          isUpdatingPost: true
        })
        await deletePost(item.postToBePublished);
        this.props.onDelete()
      }}
    />})
  };

  onSaveReject = async () => {
    const item = this.props.item;
    const rejectedText = this.state.rejectedText;
    await rejectPost(item.socialPublishingId, rejectedText);
    this.setState({ isRejecting: false });
    this.props.onReject(this.props.item);
  };

  savePost = async (isApproved = false, toastSuccessTitle, toastSuccessMessage) => {
    const item = this.props.item;
    const update = {
      ...item.postToBePublished,
      // TODO - should prob handle on the server, but currently looking at the body
      rejectedForPublishing: false,
      details: {
        ...item.postToBePublished.details,
        text: this.state.editingText !== "" ? this.state.editingText : item.text
      }
    };
    this.setState({ isEditing: false, editingText: "", isUpdatingPost: true });
    const updatedItem = isApproved === true ? await approvePost(item.socialPublishingId, update) : await updatePost(item.socialPublishingId, update);
    this.setState({ isUpdatingPost: false });

    if (toastSuccessTitle && toastSuccessMessage) {
      ToastService.success(toastSuccessTitle, toastSuccessMessage);
    } else {
      ToastService.success("Update Successful", "Your post has been updated.");
    }
    this.props.onApprove(updatedItem);
  };

  renderControls = () => {
    const item = this.props.item;
    const { isUpdatingPost } = this.state;
    return (
      <div className="mt-3">
        {
          canDeletePendingReply({ item }) && <button
          disabled={isUpdatingPost}
          className="btn btn-link text-secondary btn-sm"
          title="Delete"
          onClick={this.deletePost}
        >
          <span className="fas fa-trash-alt" />
        </button>
        }
        <button
          type="button"
          disabled={isUpdatingPost}
          className="btn btn-link text-secondary btn-sm"
          title="Edit"
          onClick={() => this.setState({isEditing: true, editingText: item.text})}
        >
          <span className="fas fa-pencil" />
        </button>
        {canApproveResponses() && (
          <Fragment>
            <button
              disabled={isUpdatingPost || item.postToBePublished.rejectedForPublishing}
              title="Reject"
              className="btn btn-outline-secondary btn-sm control-spacer-left"
              onClick={() => {
                this.setState({ isRejecting: true });
              }}
            >
              Reject
            </button>
            <button
              disabled={isUpdatingPost}
              title="Approve"
              className="btn btn-secondary btn-sm control-spacer-left"
              onClick={this.saveAndApprovePost}
            >
              Approve
            </button>
          </Fragment>
        )}
      </div>
    );
  };

  renderEditPost = () => {
    const item = this.props.item;
    const { isUpdatingPost } = this.state;
    return (
      <div className="form-group mt-3">
        <div className="chatter-publish-textarea-container compact content-spacer-bottom">
          <textarea
            className="form-control chatter-publish-textarea"
            value={this.state.editingText}
            onChange={(e) => this.setState({ editingText: e.target.value })}
            rows={3}
          />
        </div>
        <div className="content-spacer-bottom">
          <button
            title="Cancel"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => this.setState({ isEditing: false })}
          >
            Cancel
          </button>
          {canApproveResponses() && (
            <button
              type="button"
              disabled={isUpdatingPost}
              title="Save and Approve"
              className="btn btn-sm btn-secondary control-spacer-left"
              onClick={this.saveAndApprovePost}
            >
              Save &amp; Approve
            </button>
          )}
          {canDraftResponses() && !canApproveResponses() && (
            <button
              type="button"
              title="Save"
              disabled={isUpdatingPost}
              className="btn btn-sm btn-secondary control-spacer-left"
              onClick={this.savePost}
            >
              {item.rejectedForPublishing ? "Resubmit" : "Save"}
            </button>
          )}
        </div>
      </div>
    );
  };

  renderRejectionEditor = () => {
    const { rejectedText } = this.state;

    return (
      <Fragment>
        <div className={"input-group"}>
          <label
            className="col-md-12 mt-2 no-padding"
            htmlFor="rejected-reason-text"
          >
            Reason for Rejection <span className="text-danger">(required)</span>
          </label>
          <input
            id="rejected-reason-text"
            className="mt-2 mb-2 form-control"
            type="textarea"
            name="text"
            value={rejectedText}
            onChange={e => {
              this.setState({ rejectedText: e.target.value });
            }}
          />
        </div>
        <div className="row">
          <div className="form-group col">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() =>
                this.setState({ rejectedText: "", isRejecting: false })
              }
            >
              Cancel
            </button>
            <button
              disabled={!rejectedText.trim().length}
              type="button"
              className="btn btn-sm btn-secondary control-spacer-left"
              onClick={this.onSaveReject}
            >
              Reject
            </button>
          </div>
        </div>
      </Fragment>
    );
  };
  render() {
    const item = this.props.item;
    const { isRejecting, isEditing } = this.state;
    const draft = item.postToBePublished;
    if (
      !draft ||
      !item.isUnpublished ||
      !draft.requiresApproval ||
      !canDraftResponses()
    ) {
      return null;
    } else if (isEditing) {
      return this.renderEditPost();
    } else if (isRejecting) {
      return this.renderRejectionEditor();
    } else {
      return this.renderControls();
    }
  }
});
